import Imgix from '../core/Imgix';
import Swiper from 'swiper';
import { debounce } from '../utility/UtilityFunctions';
import { Autoplay, Navigation, Pagination, Keyboard, Mousewheel, Scrollbar } from 'swiper/modules';

export default class Slider {
    constructor(el) {
        this.element = el
        this.imagesRequested = false
        this.swiper = null
        this.imgixInstance = new Imgix(); // register new Imgix instance

        // Bind debounced resize handler
        this.onResize = debounce(this.checkSlideCountAndWarn.bind(this), 250);

        this.options = this.getDefaultOptions();
        this.applyCustomOptions();
        this.initSlider();

        // Register resize event
        window.addEventListener('resize', this.onResize);
    }

    getDefaultOptions() {
        return {
            modules: [Navigation, Pagination, Keyboard, Mousewheel],
            loop: true,
            slidesPerView: 1,
            effect: "fade",
            centeredSlides: true,
            preventInteractionOnTransition: true, // ! be careful this isn't a breaking addition (prevents slide skips for partial slides (1.2 perView etc))
            pagination: {
                el: '.swiper-pagination',
                type: 'fraction'
            },
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            keyboard: {
                enabled: true,
                onlyInViewport: true,
            },
            mousewheel: {
                forceToAxis: true,       // Only scrolls on the intended axis (prevents weird diagonal behavior)
                sensitivity: 0.5,        // Reduces scroll sensitivity (default is 1). Lower = smoother.
                releaseOnEdges: true,    // Allows page scrolling when at Swiper edge
                thresholdDelta: 10,      // Minimum scroll delta to trigger
                thresholdTime: 200       // Throttle scroll events (ms)
            },
            on: {
                slideChange: (swiper) => this.handleSlideChange(swiper),

                // debugging functions
                // init: (swiper) => this.debugSwiper(swiper),
                // slideChange: (swiper) => console.log('Slide changed to:', swiper.activeIndex),
                // navigationNext: () => console.log('Next button clicked'), // triggers twice on click
                // navigationPrev: () => console.log('Prev button clicked'), // triggers twice on click
            },
        };
    }

    checkSlideCountAndWarn() {
		const slideCount = this.element.querySelectorAll('.swiper-slide').length;
		const breakpoints = this.options.breakpoints || {}
		const viewportWidth = window.innerWidth

		// Determine effective slidesPerView
        let slidesPerView = this.options?.slidesPerView === 'auto' ? 1 : (this.options?.slidesPerView || 1);

		// Compare against breakpoint slidesPerView if set
		for (const breakpoint in breakpoints) {
			const bp = parseInt(breakpoint, 10)
			if (
				viewportWidth >= bp &&
				breakpoints[bp].slidesPerView !== undefined
			) {
				slidesPerView = breakpoints[bp].slidesPerView
			}
		}

		const notEnoughSlides = slideCount < Math.ceil(slidesPerView) + 1
		this.element.classList.toggle(
			"swiper--not-enough-slides",
			notEnoughSlides
		)
	}

    applyCustomOptions() {
        const classList = this.element.parentElement.classList;

        let configUpdates = {};

        switch (true) {
            case classList.contains('c-highlights-hero'):
                configUpdates = {
                    modules: [Navigation, Pagination],
                    centeredSlides: false,
                    breakpoints: {
                        1024: {
                            spaceBetween: 16,
                            slidesPerView: 2,
                        },
                    },
                };
                break;
            
            case classList.contains('c-booking-overlay__benefits'):
            case classList.contains('c-highlights-hero__book-direct'):
                configUpdates = {
                    modules: [Pagination, Autoplay],
                    loop: true,
                    centeredSlides: false,
                    slidesPerView: 1,
                    spaceBetween: 0,
                    autoplay: {
                        delay: 4000,
                    },
                    navigation: {
                        enabled: false,
                    },
                    pagination: {
                        el: ".swiper-pagination",
                        type: "bullets",
                    },
                };
                break;

            case classList.contains('c-content-slider'):
                configUpdates = {
                    modules: [Navigation, Keyboard, Scrollbar, Mousewheel],
                    loop: false,
                    centeredSlides: false,
                    slidesPerView: 1.2,
                    spaceBetween: 30,
                    pagination: {
                        enabled: false,
                    },
                    breakpoints: {
                        765: { slidesPerView: 2 },
                        1024: { slidesPerView: 2.5 },
                    },
                    scrollbar: {
                        el: '.swiper-scrollbar',
                        draggable: true,
                        dragSize: 'auto',
                        hide: false,
                    },
                };
                break;
        }

        Object.assign(this.options, configUpdates);
    }

    handleSlideChange(swiper) {
        if (this.imagesRequested) return;

        this.imgixInstance.process(swiper.el);
        this.imagesRequested = true;
        this.handleVideoPlayback(swiper);
    }

    handleVideoPlayback(swiper) {
        const currentSlide = swiper.slides[swiper.activeIndex];
        if (!currentSlide) return;

        const videoElement = currentSlide.querySelector('video');

        swiper.slides.forEach(slide => {
            const video = slide.querySelector('video');
            if (video && video !== videoElement) video.pause();
        });

        if (videoElement && videoElement.paused) {
            requestAnimationFrame(() => {
                videoElement.play().catch(error => console.warn('Video play() interrupted:', error));
            });
        }
    }

    initSlider() {
        Swiper.use(this.options.modules);
        this.swiper = new Swiper(this.element, this.options);
        this.swiper.init();

        if ('autoplay' in this.options || (Array.isArray(this.options.modules) && this.options.modules.includes(Autoplay))) {
            this.swiper.autoplay.start();
        }
    }

    // debugSwiper(swiper) {
    //     // Override slideNext and slidePrev to log when they are called
    //     const originalSlideNext = swiper.slideNext;
    //     const originalSlidePrev = swiper.slidePrev;

    //     swiper.slideNext = (speed, runCallbacks) => {
    //         console.log(`slideNext() called | Speed: ${speed}, Run Callbacks: ${runCallbacks}`);
    //         return originalSlideNext.call(swiper, speed, runCallbacks);
    //     };

    //     swiper.slidePrev = (speed, runCallbacks) => {
    //         console.log(`slidePrev() called | Speed: ${speed}, Run Callbacks: ${runCallbacks}`);
    //         return originalSlidePrev.call(swiper, speed, runCallbacks);
    //     };

    //     // Log all Swiper events
    //     swiper.onAny((eventName, ...args) => {
    //         console.log(`Swiper Event: ${eventName}`, args);
    //     });
    // }
}
